import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { getProjectRepository } from "../data/projectRepository";
import { InterventionPlansPage } from "../features/intervention-plans/InterventionPlansPage";
import { ResourceRoomCoverPage } from "../features/projects/ResourceRoomCoverPage";
import { ResourceRoomProjectHome } from "../features/projects/ResourceRoomProjectHome";
import { ResourceRoomProjectsPage } from "../features/projects/ResourceRoomProjectsPage";
import { ResourceDataPage } from "../features/resources/ResourceDataPage";
import { StudentTimetablePage } from "../features/student-timetables/StudentTimetablePage";
import { ResourceRoomTimetablePage } from "../features/timetable/ResourceRoomTimetablePage";

function StorageGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [state, setState] = useState<
    { status: "checking" | "ready" | "error"; message?: string }
  >({ status: "checking" });

  useEffect(() => {
    let active = true;
    void getProjectRepository().then(
      () => active && setState({ status: "ready" }),
      (error: unknown) =>
        active &&
        setState({
          status: "error",
          message:
            error instanceof Error
              ? error.message
              : "无法使用当前浏览器的本地课表存储"
        })
    );
    return () => {
      active = false;
    };
  }, []);

  if (state.status === "checking") {
    return (
      <main className="page-shell">
        <p className="state-message">正在打开当前浏览器中的课表…</p>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="page-shell">
        <section className="empty-card compact" role="alert">
          <p className="eyebrow">本地数据暂不可用</p>
          <h1>无法安全打开课表</h1>
          <p>{state.message}</p>
          <p>请确认没有禁用本站存储，或改用最新版 Chrome、Edge 后重试。现有课表没有被改动。</p>
        </section>
      </main>
    );
  }
  return <>{children}</>;
}

function CurrentRoute(): React.JSX.Element {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const projectMatch = path.match(/^\/projects\/([0-9a-f-]+)$/i);
  if (projectMatch?.[1]) return <ResourceRoomProjectHome projectId={projectMatch[1]} />;
  const featureMatch = path.match(
    /^\/projects\/([0-9a-f-]+)\/(student-timetables|course-natures|resources|intervention-plans|timetables)$/i
  );
  if (featureMatch?.[1] && featureMatch[2] === "student-timetables") {
    return <StudentTimetablePage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "course-natures") {
    return <StudentTimetablePage projectId={featureMatch[1]} initialTask="COURSE_NATURES" />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "resources") {
    return <ResourceDataPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "intervention-plans") {
    return <InterventionPlansPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "timetables") {
    return <ResourceRoomTimetablePage projectId={featureMatch[1]} />;
  }
  if (path === "/projects") return <ResourceRoomProjectsPage />;
  return (
    <main className="page-shell">
        <a className="back-link" href="/projects">← 返回学期项目</a>
      <section className="empty-card compact">
        <h1>页面不存在</h1>
      </section>
    </main>
  );
}

export function App(): React.JSX.Element {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 15_000 }
        }
      })
  );

  const isCover = (window.location.pathname.replace(/\/+$/, "") || "/") === "/";
  // 纯展示判断：位于某个项目内时，顶栏右侧提供返回项目库的入口
  const inProject = /^\/projects\/[0-9a-f-]+/i.test(window.location.pathname);

  return (
    <QueryClientProvider client={client}>
      {isCover ? (
        <ResourceRoomCoverPage />
      ) : (
        <div className="app-frame">
          <header className="app-topbar">
            <div className="app-topbar-inner">
              <a className="brand-link" href="/" aria-label="七维教育与融合星球首页">
                <span className="brand-icon" aria-hidden="true">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="17" rx="2" />
                    <path d="M3 9h18M8 2v4M16 2v4M7 13h4M7 17h7" />
                  </svg>
                </span>
                <span className="brand-name">七维教育&amp;融合星球</span>
              </a>
              {inProject ? (
                <a className="topbar-back" href="/projects">
                  ← 全部学期项目
                </a>
              ) : null}
            </div>
          </header>
          <StorageGate>
            <CurrentRoute />
          </StorageGate>
        </div>
      )}
    </QueryClientProvider>
  );
}
